/**
 * Bảo mật CCCD phía client (Spark — không Cloud Functions):
 * HMAC-SHA-256 với pepper REACT_APP_CCCD_PEPPER + mask hiển thị.
 * Pepper nằm trong bundle CRA nên không phải secret server-side thật;
 * vẫn tốt hơn lưu plaintext và cho phép tra cứu bằng hash.
 */

/** Chỉ giữ chữ số (NFKC: số fullwidth → ASCII). */
export function chuanHoaCccd(raw) {
  return String(raw ?? "")
    .normalize("NFKC")
    .replace(/\D/g, "");
}

/** Pepper đã được CRA nhúng vào bundle (cần restart `npm start` / build lại sau khi sửa .env). */
export function coPepperCccd() {
  const pepper = process.env.REACT_APP_CCCD_PEPPER;
  return Boolean(pepper && String(pepper).trim());
}

/**
 * HMAC-SHA-256 (hex) của CCCD đã chuẩn hoá.
 * @throws nếu thiếu REACT_APP_CCCD_PEPPER hoặc CCCD trống sau chuẩn hoá
 */
export async function hashCccd(cccd) {
  const pepper = process.env.REACT_APP_CCCD_PEPPER;
  if (!coPepperCccd()) {
    throw new Error(
      "Thiếu REACT_APP_CCCD_PEPPER — thêm chuỗi bí mật dài ngẫu nhiên vào frontend/.env, rồi restart npm start hoặc build+deploy lại (không commit giá trị thật)."
    );
  }
  const digits = chuanHoaCccd(cccd);
  if (!digits) {
    throw new Error("CCCD trống sau khi chuẩn hoá");
  }
  if (typeof crypto === "undefined" || !crypto.subtle) {
    throw new Error("Trình duyệt không hỗ trợ Web Crypto (cần HTTPS hoặc localhost).");
  }

  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    "raw",
    enc.encode(String(pepper)),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  const sig = await crypto.subtle.sign("HMAC", key, enc.encode(digits));
  return [...new Uint8Array(sig)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Mask hiển thị: 3 số đầu + sao giữa + 3 số cuối (vd 12 số → 079******123).
 */
export function maskCccd(cccd) {
  const digits = chuanHoaCccd(cccd);
  if (!digits) return "";
  if (digits.length <= 6) {
    return `${digits.slice(0, 1)}${"*".repeat(Math.max(0, digits.length - 2))}${digits.slice(-1)}`;
  }
  const middleLen = digits.length - 6;
  return `${digits.slice(0, 3)}${"*".repeat(middleLen)}${digits.slice(-3)}`;
}

/** Keyword có vẻ là CCCD (đủ dài để tra hash, tránh đụng SĐT ngắn). */
export function coVeLaCccd(raw) {
  return chuanHoaCccd(raw).length >= 9;
}

/**
 * Số CCCD đủ từ document thô (field hoặc id cũ = CCCD). Bỏ chuỗi có dấu *.
 */
export function soCccdTuDuLieuTho(id, data = {}) {
  const ungVien = [data.cccd, data.cccdDayDu, data.soCccd, data.soCCCD, data.CCCD, data.cmnd, id];
  for (const v of ungVien) {
    if (v == null || v === "") continue;
    const s = String(v);
    if (s.includes("*")) continue;
    const d = chuanHoaCccd(s);
    if (d.length >= 9 && d.length <= 12) return d;
  }
  return "";
}

/**
 * Số CCCD đủ cho chứng từ thuế / admin.
 * Ưu tiên field plaintext; hồ sơ cũ thường chỉ lưu CCCD ở document id.
 */
export function soCccdDayDuTuHoSo(cv) {
  if (!cv) return "";
  const tuDayDu = chuanHoaCccd(cv.cccdDayDu);
  if (tuDayDu.length >= 9 && tuDayDu.length <= 12) return tuDayDu;
  const hienThi = String(cv.cccd ?? "");
  if (hienThi && !hienThi.includes("*")) {
    const tuField = chuanHoaCccd(hienThi);
    if (tuField.length >= 9 && tuField.length <= 12) return tuField;
  }
  const idStr = String(cv.id ?? "");
  if (/^\d{9,12}$/.test(idStr)) return idStr;
  return "";
}

const cacheKhoiPhuc = new Map();

/**
 * Khôi phục số đủ từ HMAC + mask (3 đầu / 3 cuối). Tối đa 6 số giữa (~1e6).
 * Dùng khi migrate đã xoá plaintext nhưng còn hash+mask — cần cho bảng kê thuế.
 */
export async function khoiPhucCccdTuHashVaMask(cccdHash, cccdMask) {
  const pepper = process.env.REACT_APP_CCCD_PEPPER;
  const hashTarget = String(cccdHash ?? "")
    .trim()
    .toLowerCase();
  const mask = String(cccdMask ?? "");
  if (!pepper || !String(pepper).trim() || !hashTarget || !mask.includes("*")) return "";
  if (typeof crypto === "undefined" || !crypto.subtle) return "";

  const prefix = (mask.match(/^\d+/) || [""])[0];
  const suffix = (mask.match(/\d+$/) || [""])[0];
  const middleLen = (mask.match(/\*/g) || []).length;
  if (!prefix || !suffix || middleLen < 1 || middleLen > 6) return "";

  const cacheKey = `${hashTarget}|${mask}`;
  if (cacheKhoiPhuc.has(cacheKey)) return cacheKhoiPhuc.get(cacheKey);

  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    "raw",
    enc.encode(String(pepper)),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );

  const hexCua = (buf) =>
    [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");

  const thu = async (i) => {
    const guess = `${prefix}${String(i).padStart(middleLen, "0")}${suffix}`;
    const sig = await crypto.subtle.sign("HMAC", key, enc.encode(guess));
    return hexCua(sig) === hashTarget ? guess : "";
  };

  const max = 10 ** middleLen;
  const kichThuoc = 400;
  for (let start = 0; start < max; start += kichThuoc) {
    const n = Math.min(kichThuoc, max - start);
    const ketQua = await Promise.all(Array.from({ length: n }, (_, j) => thu(start + j)));
    const trung = ketQua.find(Boolean);
    if (trung) {
      cacheKhoiPhuc.set(cacheKey, trung);
      return trung;
    }
  }
  cacheKhoiPhuc.set(cacheKey, "");
  return "";
}

export async function soCccdDayDuAsync(cv) {
  const nhanh = soCccdDayDuTuHoSo(cv);
  if (nhanh) return nhanh;
  return khoiPhucCccdTuHashVaMask(cv?.cccdHash, cv?.cccdMask);
}

/**
 * Admin xem số đủ nếu hồ sơ còn (field hoặc id = CCCD).
 * Vai trò khác — UI thường — chỉ mask.
 */
export function hienThiCccd(cv, vaiTro) {
  if (vaiTro === "admin") {
    return soCccdDayDuTuHoSo(cv) || cv?.cccdMask || cv?.cccd || "";
  }
  return cv?.cccdMask || cv?.cccd || "";
}
